"use client";
import clsx from "clsx";
import { useEffect, useState } from "react";
import {
  SizeGuide,
  HowToMeasureCategory,
  getSizeGuide,
} from "@repo/apis/size-management-api-v1";
import * as Accordion from "@repo/ui/accordion";
import { Button } from "@repo/ui/button";
import { CloudinaryImage } from "@repo/ui/cloudinary-image";
import * as Flex from "@repo/ui/flex";
import { InlineMessage } from "@repo/ui/inline-message";
import * as Sheet from "@repo/ui/sheet";
import * as Switch from "@repo/ui/switch";
import { VisuallyHidden } from "@repo/ui/visually-hidden";
import { getSizeGuideId } from "@repo/utils/get-size-guide-id";
import { THUMBNAIL } from "@/constants";
import { CloseIcon } from "@/theme/icons/close";
import { SpinnerLargeIcon } from "@/theme/icons/spinner-large";
import { WarningIcon } from "@/theme/icons/warning";
import {
  TYPOGRAPHY_HEADING2,
  TYPOGRAPHY_LINK2,
  TYPOGRAPHY_BODY3_BOLD,
  TYPOGRAPHY_BODY3_SENTENCE,
} from "@/theme/topshop/typography/typography-constants";
import { clientRequest } from "@/utils/request-adapters/client-request";
import classes from "./size-guide.module.css";

type TransformedSizeData = {
  [unit: string]: {
    sizeLabels: string[];
    dimensionLabels: string[];
    data: {
      [brandSizeText: string]: {
        [dimensionText: string]: string;
      };
    };
  };
};

type TransformedInternationalConversionsData = {
  sizeLabels: string[];
  countrySchemas: string[];
  data: {
    [brandSizeText: string]: {
      [schema: string]: string;
    };
  };
};

const transformSizeGuideData = (sizeGuide: SizeGuide): TransformedSizeData => {
  const result: TransformedSizeData = {};
  const allSizeLabels = new Set<string>();
  const allDimensionLabels = new Set<string>();

  sizeGuide.sizes.forEach((size) => {
    allSizeLabels.add(size.brandSizeText);

    size.dimensions.forEach((dimension) => {
      allDimensionLabels.add(dimension.dimensionText);

      dimension.measurements.forEach((measurement) => {
        if (!result[measurement.unit]) {
          result[measurement.unit] = {
            sizeLabels: [],
            dimensionLabels: [],
            data: {},
          };
        }

        if (!result[measurement.unit]?.data[size.brandSizeText]) {
          result[measurement.unit]!.data[size.brandSizeText] = {};
        }

        result[measurement.unit]!.data[size.brandSizeText]![
          dimension.dimensionText
        ] = measurement.value;
      });
    });
  });

  Object.keys(result).forEach((unit) => {
    result[unit]!.sizeLabels = Array.from(allSizeLabels);
    result[unit]!.dimensionLabels = Array.from(allDimensionLabels);
  });

  return result;
};

const transformInternationalConversionsData = (
  sizeGuide: SizeGuide,
): TransformedInternationalConversionsData => {
  const allSizeLabels = new Set<string>();
  const allSchemas = new Set<string>();
  const data: { [brandSizeText: string]: { [schema: string]: string } } = {};

  sizeGuide.sizes.forEach((size) => {
    allSizeLabels.add(size.brandSizeText);
    data[size.brandSizeText] = {};

    size.conversions.forEach((conversion) => {
      allSchemas.add(conversion.schema);
      data[size.brandSizeText]![conversion.schema] =
        conversion.internationalConversionText;
    });
  });

  return {
    sizeLabels: Array.from(allSizeLabels),
    countrySchemas: setPreferredOrder(Array.from(allSchemas)),
    data,
  };
};

export const setPreferredOrder = (schemas: string[]): string[] => {
  const ukSchema = schemas.find(
    (schema) =>
      schema.toLowerCase().includes("uk") || schema.toLowerCase() === "uk",
  );

  if (!ukSchema) {
    return schemas;
  }

  return [ukSchema, ...schemas.filter((schema) => schema !== ukSchema)];
};

type SizeGuideSheetProps = {
  sizeGuideUrl: string;
};

export const SizeGuideSheet = ({ sizeGuideUrl }: SizeGuideSheetProps) => {
  const [data, setData] = useState<SizeGuide | null>(null);
  const [transformedData, setTransformedData] =
    useState<TransformedSizeData | null>(null);
  const [internationalConversionsData, setInternationalConversionsData] =
    useState<TransformedInternationalConversionsData | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState<"CM" | "IN">("CM");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setIsLoading(false);
    setError(null);
    const fetchData = async (sizeGuideId: number) => {
      try {
        setError(null);

        const response = await getSizeGuide({
          sizeGuideId: sizeGuideId,
          chameleonRequestInstance: clientRequest,
        });

        if (!response) {
          setError("Error loading size guide");
          setIsLoading(false);
          return;
        }

        setData(response);
        setTransformedData(transformSizeGuideData(response));
        setInternationalConversionsData(
          transformInternationalConversionsData(response),
        );
        setIsLoading(false);
      } catch (error) {
        setError("Error loading size guide");
        setIsLoading(false);
      }
    };

    if (isOpen) {
      if (!data) {
        setIsLoading(true);
        setError(null);

        const sizeGuideId = getSizeGuideId({ sizeGuideUrl });

        if (!sizeGuideId) {
          setError("Error loading size guide");
          setIsLoading(false);
        } else {
          fetchData(sizeGuideId);
        }
      }
    }
  }, [isOpen, data, sizeGuideUrl]);

  return (
    <Sheet.Root open={isOpen} onOpenChange={setIsOpen}>
      <Sheet.Trigger
        className={clsx(TYPOGRAPHY_LINK2, classes.sizeGuideTrigger)}
      >
        Size Guide
      </Sheet.Trigger>
      <Sheet.Content
        position="right"
        overlayAnimation
        className={classes.sizeGuideSheet}
      >
        <Flex.Container column className={classes.loadingContainer}>
          <Sheet.Header>
            <Flex.Container justify="end" align="center">
              <VisuallyHidden>
                <Sheet.Title className={TYPOGRAPHY_HEADING2}>
                  Size Guide
                </Sheet.Title>
                <Sheet.Description>
                  View size guide information
                </Sheet.Description>
              </VisuallyHidden>
              <Sheet.Close asChild>
                <Button size="icon" className={classes.closeIcon}>
                  <CloseIcon label="Close Size Guide" />
                </Button>
              </Sheet.Close>
            </Flex.Container>
          </Sheet.Header>

          {isLoading ? (
            <Flex.Container
              justify="center"
              align="center"
              className={classes.loadingContainer}
            >
              <SpinnerLargeIcon
                label="Size Guide is Loading"
                className={classes.spinnerIcon}
              />
            </Flex.Container>
          ) : error ||
            !data ||
            !transformedData ||
            !internationalConversionsData ? (
            <Flex.Container
              justify="center"
              align="center"
              className={classes.loadingContainer}
            >
              {error ? (
                <InlineMessage
                  className={clsx(
                    TYPOGRAPHY_BODY3_SENTENCE,
                    classes.errorMessage,
                  )}
                  icon={
                    <WarningIcon className={classes.errorIcon} label="Error" />
                  }
                  variant="error"
                >
                  {error}
                </InlineMessage>
              ) : null}
            </Flex.Container>
          ) : (
            <Sheet.Main className={classes.sizeGuideSheetMain}>
              <Flex.Container column gap="xl">
                {data.sizes[0] &&
                  data.sizes[0].dimensions.length > 0 &&
                  data.howToMeasureByCategory.length > 0 && (
                    <Flex.Container column gap="md">
                      <h2 className={TYPOGRAPHY_HEADING2}>
                        {data.termsDictionary.bodyMeasurements}
                      </h2>
                      <Flex.Container gap="sm">
                        <Switch.Root
                          aria-label="Dimension Switch"
                          className={classes.root}
                          id="dimension-switch"
                          checked={selectedUnit === "CM"}
                          onCheckedChange={(checked) =>
                            setSelectedUnit(checked ? "CM" : "IN")
                          }
                        >
                          <label
                            className={clsx(
                              classes.switchlabelCm,
                              TYPOGRAPHY_BODY3_BOLD,
                            )}
                            htmlFor="dimension-switch"
                          >
                            {data.termsDictionary.centimetres}
                          </label>
                          <label
                            className={clsx(
                              classes.switchlabelIn,
                              TYPOGRAPHY_BODY3_BOLD,
                            )}
                            htmlFor="dimension-switch"
                          >
                            {data.termsDictionary.inches}
                          </label>
                          <Switch.Thumb className={classes.Thumb} />
                        </Switch.Root>
                      </Flex.Container>

                      {transformedData[selectedUnit] && (
                        <div className={classes.sizeGuideTableConatiner}>
                          <table className={classes.bodyMeasurementsTable}>
                            <thead>
                              <tr>
                                <th
                                  className={clsx(
                                    classes.sizeGuideTableCell,
                                    classes.sizeGuideTableCellHead,
                                  )}
                                >
                                  {data.termsDictionary.size}
                                </th>
                                {transformedData[
                                  selectedUnit
                                ]?.dimensionLabels.map((dimensionText) => (
                                  <th
                                    className={clsx(
                                      classes.sizeGuideTableCell,
                                      classes.sizeGuideTableCellHead,
                                    )}
                                    key={dimensionText}
                                  >
                                    {dimensionText}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody className={classes.sizeGuideTableTBody}>
                              {transformedData[selectedUnit]?.sizeLabels.map(
                                (sizeLabel) => (
                                  <tr key={sizeLabel}>
                                    <td className={classes.sizeGuideTableCell}>
                                      {sizeLabel}
                                    </td>
                                    {transformedData[
                                      selectedUnit
                                    ]?.dimensionLabels.map((dimensionText) => (
                                      <td
                                        className={classes.sizeGuideTableCell}
                                        key={`${sizeLabel}-${dimensionText}`}
                                      >
                                        {transformedData[selectedUnit]?.data[
                                          sizeLabel
                                        ]?.[dimensionText] || "-"}
                                      </td>
                                    ))}
                                  </tr>
                                ),
                              )}
                            </tbody>
                          </table>
                        </div>
                      )}

                      <a href="#howToMeasure" className={TYPOGRAPHY_LINK2}>
                        {data.termsDictionary.howToMeasure}
                      </a>
                    </Flex.Container>
                  )}
                <Flex.Container column gap="md">
                  <h2 className={TYPOGRAPHY_HEADING2}>
                    {data.termsDictionary.internationalConversions}
                  </h2>
                  {internationalConversionsData && (
                    <div className={classes.sizeGuideTableConatiner}>
                      <table className={classes.internationalConversionsTable}>
                        <thead>
                          <tr>
                            <th
                              className={clsx(
                                classes.sizeGuideTableCell,
                                classes.sizeGuideTableCellHead,
                              )}
                            >
                              {data.termsDictionary.size}
                            </th>
                            {internationalConversionsData.countrySchemas.map(
                              (schema) => (
                                <th
                                  className={clsx(
                                    classes.sizeGuideTableCell,
                                    classes.sizeGuideTableCellHead,
                                  )}
                                  key={schema}
                                >
                                  {schema}
                                </th>
                              ),
                            )}
                          </tr>
                        </thead>
                        <tbody className={classes.sizeGuideTableTBody}>
                          {internationalConversionsData.sizeLabels.map(
                            (sizeLabel) => (
                              <tr key={sizeLabel}>
                                <td className={classes.sizeGuideTableCell}>
                                  {sizeLabel}
                                </td>
                                {internationalConversionsData.countrySchemas.map(
                                  (schema) => (
                                    <td
                                      className={classes.sizeGuideTableCell}
                                      key={`${sizeLabel}-${schema}`}
                                    >
                                      {internationalConversionsData.data[
                                        sizeLabel
                                      ]?.[schema] || "-"}
                                    </td>
                                  ),
                                )}
                              </tr>
                            ),
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Flex.Container>
                {data.sizes[0] &&
                  data.sizes[0].dimensions.length > 0 &&
                  data.howToMeasureByCategory.length > 0 && (
                    <Flex.Container
                      column
                      gap="md"
                      id="howToMeasure"
                      className={classes.howToMeasureSection}
                    >
                      <h2 className={TYPOGRAPHY_HEADING2}>
                        {data.termsDictionary.howToMeasure}
                      </h2>
                      <Accordion.Root>
                        {data.howToMeasureByCategory.map(
                          (item: HowToMeasureCategory) => (
                            <Accordion.Item
                              value={item.category}
                              key={item.category}
                            >
                              <Accordion.Header>
                                <Accordion.Trigger>
                                  <span>{item.category}</span>
                                  <Accordion.TriggerIcon />
                                </Accordion.Trigger>
                              </Accordion.Header>
                              <Accordion.Content>
                                <Flex.Container column gap="md">
                                  <p>{item.note}</p>
                                  <CloudinaryImage
                                    alt={item.category}
                                    url={item.imageUrl}
                                    sourceSetProfile={THUMBNAIL}
                                    srcSize={10}
                                    className={classes.howToMeasureImage}
                                  />

                                  {item.list.map((listItem, index) => (
                                    <p key={index}>{listItem}</p>
                                  ))}
                                </Flex.Container>
                              </Accordion.Content>
                            </Accordion.Item>
                          ),
                        )}
                      </Accordion.Root>
                    </Flex.Container>
                  )}
              </Flex.Container>
            </Sheet.Main>
          )}
        </Flex.Container>
      </Sheet.Content>
    </Sheet.Root>
  );
};
